import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ThreadEntry } from "../../../api/threads/liveMessages";
import { answerHumanStep } from "../../../api/threads/threadsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import { FORM_COMPACT_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import MergeActions from "../extractions/MergeActions";
import type { ResultChoice } from "./resultChoice";

interface ResultDecisionProps {
  readonly threadId: string;
  readonly entry: ThreadEntry;
  /** The server says this person may decide it. */
  readonly canDecide: boolean;
  /** Accepting is offered: not for a vita assembled from only some of its parts. */
  readonly acceptable: boolean;
  /** A correction is merged, after confirming how many newer proposals it passes over. */
  readonly merge: { readonly newerCount: number } | null;
  /** Shown to someone who may not decide what still waits for a decision. */
  readonly notCurator: string | null;
  /** The decision already made while the step still waits (a stop between the two writes). */
  readonly recorded: { readonly kind: ResultChoice; readonly comment: string } | undefined;
  /** The review asks for a comment with every decision (the common review part's rule, O1). */
  readonly commentRequired: boolean;
}

/**
 * The answer of an acceptance step (ADR-0026 slice 3a): Accept (Merge, for a correction) or
 * Reject with a reason, for whoever may decide; otherwise why not, or recording a decision
 * already made. One answer, whatever it decides: one result or every part of a vita (E4).
 * Where the review asks for a comment, no decision goes without one.
 */
export default function ResultDecision({ threadId, entry, canDecide, acceptable, merge, notCurator, recorded, commentRequired }: ResultDecisionProps) {
  const { t } = useTranslation();
  const submission = useSubmission();
  const [comment, setComment] = useState("");
  const busy = submission.state.status === "submitting" || submission.state.status === "succeeded";
  const commentMissing = comment.trim() === "";
  const commentLabel = commentRequired ? t("threads.humanStep.commentRequired") : t("data.curation.comment");
  const decide = (choice: ResultChoice, reason: string = comment) => {
    submission.submit(async () => {
      // One key per request and decision, so a retry replays a decision the server already made.
      await answerHumanStep(threadId, { answersEntry: entry.number, comment: reason.trim() === "" ? null : reason.trim(), choice, result: null, clientRequestId: `result-${String(entry.number)}-${choice}` });
    });
  };
  return <>
    {canDecide ? (
      <div className="space-y-2">
        <textarea
          className={FORM_COMPACT_CONTROL_CLASS}
          rows={2}
          value={comment}
          aria-label={commentLabel}
          placeholder={commentLabel}
          onChange={(event) => {
            setComment(event.target.value);
          }}
        />
        <div className="flex gap-2">
          {merge !== null ? (
            <MergeActions newerCount={merge.newerCount} disabled={busy || !acceptable || (commentRequired && commentMissing)} onMerge={() => { decide("accept"); }} />
          ) : (
            <AppButton size="sm" variant="primary" disabled={busy || !acceptable || (commentRequired && commentMissing)} onClick={() => { decide("accept"); }}>
              {t("data.curation.accept")}
            </AppButton>
          )}
          <AppButton size="sm" variant="danger" disabled={busy || commentMissing} onClick={() => { decide("reject"); }}>
            {t("data.curation.reject")}
          </AppButton>
        </div>
      </div>
    ) : notCurator !== null ? (
      <p className="app-text-muted text-sm">{notCurator}</p>
    ) : recorded !== undefined ? (
      // Decided, but the step still waits (a stop between the two writes): its answer is
      // recorded with the same key, and the server finds the decision instead of deciding again.
      <AppButton size="sm" variant="secondary" disabled={busy} onClick={() => { decide(recorded.kind, recorded.comment); }}>
        {t("data.review.record")}
      </AppButton>
    ) : null}
    {submission.state.status === "failed" ? <ErrorNotice error={submission.state.error} compact={true} /> : null}
  </>;
}
